import { useState } from "react";
import { Link } from "react-router-dom";
import { useOrganizationApi } from "@/hooks/apiHooks/useOrganizationApi";
import { Badge, Button } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import type {
    LocationApprovalResult,
    OrganizationDetail,
} from "@models/organization";
import { LocationStatus } from "@models/location";
import { ROUTES } from "@routing/routes";
import {
    APPROVE_BLOCKED_MESSAGE,
    approveErrorMessage,
    canApproveLocations,
} from "../_helpers/approve";
import { describeApprovalResult } from "../_helpers/approvalOutcome";
import { ApproveConfirmDialog } from "./ApproveConfirmDialog";

interface ApproveLocationsProps {
    organization: OrganizationDetail;
    onApproved: () => void;
}

export const ApproveLocations = ({
    organization,
    onApproved,
}: ApproveLocationsProps) => {
    const { approveOrganization } = useOrganizationApi();

    const [results, setResults] = useState<LocationApprovalResult[] | null>(
        null,
    );
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isApproving, setIsApproving] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    // DecisionLog 3.4a - the control is hidden entirely, not just disabled, for
    // orgs whose status forbids approval.
    if (!canApproveLocations(organization.status)) {
        return (
            <p className="text-sm text-neutral/70">
                {APPROVE_BLOCKED_MESSAGE[organization.status]}
            </p>
        );
    }

    const runApprove = async () => {
        setIsApproving(true);
        setErrorMessage(null);
        // Drop the previous run's rows up front: leaving them under a new error
        // alert would read as if they came from the attempt that just failed.
        setResults(null);

        try {
            // PR 327 round 6: the server's stamp of the detail this panel
            // shows, untouched, so the certificates on screen are the ones
            // marked seen. A re-read in flight has not replaced this detail,
            // so its stamp still matches the screen. Absent (an older API):
            // no stamp, the old behaviour. Never the browser's clock.
            const response = await approveOrganization(
                organization.id,
                organization.retrievedAt,
            );

            setResults(response.results);
            // Locations that flipped to Approved change the detail payload.
            onApproved();
        } catch (error) {
            setErrorMessage(approveErrorMessage(error));
        } finally {
            setIsApproving(false);
            setIsConfirmOpen(false);
        }
    };

    // Results carry only locationId; the detail payload is what names them.
    const locationName = (locationId: string) =>
        organization.locations.find((location) => location.id === locationId)
            ?.name ?? locationId;

    // An empty results array carries nothing to report - the server returns 409
    // rather than an empty list, so this only guards against a "0 approved"
    // summary if that ever changes.
    const hasResults = results !== null && results.length > 0;
    const outcomes = results
        ? results.map((result) => ({
            result,
            view: describeApprovalResult(result),
        }))
        : [];
    const approvedCount = outcomes.filter(({ view }) => view.isApproved).length;
    const failedCount = outcomes.length - approvedCount;
    // Money MAY have moved for an unconfirmed charge (review finding 1): nothing
    // here invites a retry until it is resolved. The API also refuses to charge
    // it again (it reports Unconfirmed with no vendor call), so this is
    // guidance, not the only guard.
    const hasUnconfirmed = outcomes.some(
        ({ view }) => view.kind === "unconfirmed",
    );
    // #437: a location skipped for a missing certificate, or (PR 327 round 5)
    // for one uploaded after this page loaded, failed nothing about payment,
    // so the payment guidance and the retry show only for the other failures.
    const noCertificateCount = outcomes.filter(
        ({ view }) => view.kind === "noCertificate",
    ).length;
    const unseenCertificateCount = outcomes.filter(
        ({ view }) => view.kind === "unseenCertificate",
    ).length;
    const hasPaymentFailure =
        failedCount > noCertificateCount + unseenCertificateCount;
    // What the approve will process: the org's Pending locations, plus those
    // waiting for payment, whose charge approving again retries (#434). #315
    // puts `status` on the admin detail's locations.
    const pendingPaymentLocationCount = organization.locations.filter(
        (location) => location.status === LocationStatus.PendingPayment,
    ).length;
    const pendingLocationCount =
        organization.locations.filter(
            (location) => location.status === LocationStatus.Pending,
        ).length + pendingPaymentLocationCount;
    // C6 follow-up (PO 2026-10-28): a location an admin's change of state sent
    // back to review keeps its activation (activatedAt), and the API approves
    // it with no second activation charge.
    const activatedLocationCount = organization.locations.filter(
        (location) =>
            location.status === LocationStatus.Pending &&
            Boolean(location.activatedAt),
    ).length;

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                <Button
                    color={ComponentColor.Success}
                    size={ComponentSize.SM}
                    disabled={isApproving || hasUnconfirmed}
                    onClick={() => setIsConfirmOpen(true)}
                >
                    {/* A retry is invited only for a charge: a location
                        missing its certificate waits for the customer, and
                        one with an unseen certificate is approved afresh once
                        the admin has reviewed it. */}
                    {hasPaymentFailure
                        ? "Retry approval"
                        : "Approve pending locations"}
                </Button>
                {hasUnconfirmed ? (
                    <p className="text-sm text-neutral/70">
                        Retry is unavailable until the unconfirmed charge is
                        resolved in{" "}
                        <Link
                            to={ROUTES.ADMIN_UNCONFIRMED_CHARGES}
                            className="link link-primary"
                        >
                            Unconfirmed charges
                        </Link>
                        .
                    </p>
                ) : (
                    <span className="text-sm text-neutral/70">
                        Approves each pending location.{" "}
                        {activatedLocationCount > 0
                            ? "A location that was already active before it went back to review is approved without a second charge. If the organization already pays for its membership, each other location's prorated first month is charged first."
                            : "If the organization already pays for its membership, each location's prorated first month is charged first."}
                    </span>
                )}
            </div>

            {errorMessage && (
                <p role="alert" className="text-sm text-error">
                    {errorMessage}
                </p>
            )}

            {hasResults && results && (
                <div className="space-y-2">
                    <p role="status" className="text-sm font-medium">
                        {failedCount === 0
                            ? `All ${results.length} pending ${results.length === 1 ? "location" : "locations"} approved.`
                            : `${approvedCount} of ${results.length} locations approved. ${failedCount} ${failedCount === 1 ? "is" : "are"} not approved yet.`}
                    </p>

                    {failedCount > 0 && (
                        <p className="text-sm text-neutral/70">
                            This client is unaffected and stays active.{" "}
                            {noCertificateCount > 0 &&
                                "A location without a resale certificate stays pending until the customer uploads one, then approve again. "}
                            {unseenCertificateCount > 0 &&
                                "A location whose resale certificate was uploaded since this page loaded was not approved: review the new certificate, then approve again. "}
                            {hasPaymentFailure &&
                                (hasUnconfirmed
                                    ? "Resolve the unconfirmed charge first in Unconfirmed charges, then approve again. "
                                    : "A location waiting for payment is approved automatically when the customer's card or the next membership renewal goes through, or approve again to retry its charge. ")}
                            Locations already approved are skipped, so they are
                            never charged twice.
                        </p>
                    )}

                    <ul className="divide-y divide-base-300">
                        {outcomes.map(({ result, view }) => {
                            // One message per row (review finding 4): the
                            // API's own error, when it sends one, replaces the
                            // generic copy.
                            const message = view.isApproved
                                ? null
                                : (result.error ?? view.message);

                            return (
                                <li key={result.locationId} className="py-2">
                                    <div className="flex items-center justify-between gap-3">
                                        <span>
                                            {locationName(result.locationId)}
                                        </span>
                                        {/* TODO(design): placeholder badge treatment until E5-D2 lands. */}
                                        <Badge
                                            color={view.color}
                                            size={ComponentSize.SM}
                                        >
                                            {view.label}
                                        </Badge>
                                    </div>
                                    {message && (
                                        <p
                                            className={
                                                view.color ===
                                                ComponentColor.Error
                                                    ? "mt-1 text-sm text-error"
                                                    : "mt-1 text-sm text-neutral/70"
                                            }
                                        >
                                            {message}
                                        </p>
                                    )}
                                    {view.kind === "unconfirmed" && (
                                        <Link
                                            to={ROUTES.ADMIN_UNCONFIRMED_CHARGES}
                                            className="link link-primary text-sm"
                                        >
                                            Open unconfirmed charges
                                        </Link>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {isConfirmOpen && (
                <ApproveConfirmDialog
                    orgName={organization.name}
                    pendingLocationCount={pendingLocationCount}
                    pendingPaymentLocationCount={pendingPaymentLocationCount}
                    activatedLocationCount={activatedLocationCount}
                    isProcessing={isApproving}
                    onConfirm={() => void runApprove()}
                    onClose={() => setIsConfirmOpen(false)}
                />
            )}
        </div>
    );
};
